import { NgIf, AsyncPipe } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  ApiTokenObject,
  LeeOAuthentication,
  UpsertLeeOAuthenticationRequest,
} from 'src/app/settings/models/third-parties.model';
import { getLeeOAuthentication } from 'src/app/settings/store/selectors/settings.selectors';
import { ImportComponent } from 'src/app/shared/components/import/import.component';

import * as settingsActions from '../../../../store/actions/settings.actions';

@Component({
  selector: 'app-leeo-overview',
  templateUrl: './leeo-overview.component.html',
  imports: [PageToolbarComponent, NgIf, TableComponent, AsyncPipe, TranslatePipe],
})
export class LeeoOverviewComponent implements OnInit {
  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly dialogService: DialogService,
  ) {}

  @ViewChild('statusTemplate') statusTemplate: TemplateRef<void>;
  @ViewChild('locationTemplate') locationTemplate: TemplateRef<SelectedLocation>;

  public locations: FormArray = new FormArray([]);
  public locationOptions$ = this.store.select(UserSelectors.selectLocations);
  public selectedLocation: SelectedLocation = undefined;

  public isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  public leeOAuthentication$: Observable<LeeOAuthentication> =
    this.store.select(getLeeOAuthentication);

  public leeOAuthenticationTableConfig: TableConfig;

  public hasData = false;

  ngOnInit(): void {
    this.leeOAuthentication$.subscribe((leeOAuthentication) => {
      this.leeOAuthenticationTableConfig = {
        dataSource: new MatTableDataSource<LeeOAuthentication>([leeOAuthentication]),
        columns: [
          {
            id: 'tokenId',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate(
              'settings.third-parties.lee-o-authentication-token',
            ),
          },
          {
            id: 'status',
            type: TableColumnTypes.TEMPLATE,
            template: this.statusTemplate,
            label: this.localeService.translate(
              'settings.third-parties.lee-o-authentication-status',
            ),
          },
        ],
        actions: [
          {
            id: 'delete',
            callback: (row: LeeOAuthentication) => this.deleteAuthentication(row.id),
            label: '',
            icon: 'delete',
            disabled: () => false,
            primary: false,
          },
        ],
        selectColumn: false,
        pagination: false,
      };

      this.hasData =
        leeOAuthentication && Object.keys(leeOAuthentication).length > 0 ? true : false;
    });
  }

  handleAddApiToken() {
    this.dialogService
      .dialog(ImportComponent, {
        title: this.localeService.translate('settings.third-parties.add-authentication'),
        cancelButtonText: this.localeService.translate('cancel'),
        confirmButtonText: this.localeService.translate('save'),
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm && result.result?.file) {
          let apiTokenObject: ApiTokenObject;
          const reader: FileReader = new FileReader();
          reader.onload = (e) => {
            const json = JSON.parse(e.target.result as string);
            const stringObject = JSON.stringify(json);
            apiTokenObject = JSON.parse(stringObject);
            if (
              !apiTokenObject ||
              !apiTokenObject.api_token ||
              Object.keys(apiTokenObject).length === 0 ||
              Object.keys(apiTokenObject?.api_token).length === 0
            ) {
              this.dialogService
                .notify({
                  title: this.localeService.translate(
                    'settings.third-parties.lee-o-upload-error-title',
                  ),
                  text: this.localeService.translate(
                    'settings.third-parties.lee-o-upload-error-message',
                  ),
                })
                .pipe(take(1))
                .subscribe();
            } else {
              this.store.dispatch(
                settingsActions.upsertLeeOAuthenticationRequest({
                  upsertLeeOAuthenticationRequest: {
                    tokenId: apiTokenObject.api_token.id,
                    accessToken: apiTokenObject.api_token.token,
                    accessTokenExpiryDate: apiTokenObject.api_token.expires_at,
                    refreshToken: apiTokenObject.api_token.refresh_token,
                    refreshUrl: apiTokenObject.api_token.refresh_url,
                  } as UpsertLeeOAuthenticationRequest,
                }),
              );
            }
          };
          reader.readAsText(result.result?.file);
        }
      });
  }

  deleteAuthentication(leeOAuthenticationId: number) {
    this.dialogService
      .confirmWarning({
        title: this.localeService.translate(
          'settings.third-parties.lee-o-delete-confirmation-title',
        ),
        text: this.localeService.translate(
          'settings.third-parties.lee-o-delete-confirmation-message',
        ),
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(
            settingsActions.deleteLeeOAuthenticationRequest({
              leeOAuthenticationId,
            }),
          );
        }
      });
  }
}
